import { Cart } from "../models/Cart";
import { Department, SimpleDepartment } from "../models/Event";
import { Order } from "../models/Order";

interface GTMConfig {
    containerId?: string;
}

class GTM {
    CONTAINER_ID?: string;
    CLIENT_CONTAINER_ID?: string;
    initialized = false;
    initializedClient = false;

    configure(config: GTMConfig) {
        if (!config.containerId) {
            console.warn("Must provide a GTM Cotainer ID.");
            return;
        }
        this.CONTAINER_ID = config.containerId;
    }

    configureClient(config: GTMConfig) {
        if (!config.containerId) {
            console.warn("Must provide a GTM Cotainer ID.");
            return;
        }
        this.CLIENT_CONTAINER_ID = config.containerId;
    }

    initialize(config: GTMConfig) {
        if (this.initialized) {
            console.warn("GTM already initialized.");
            return;
        }

        if (!document) {
            console.warn("GTM requires a document.");
            return;
        }

        this.configure(config);

        if (!this.CONTAINER_ID) {
            console.warn("GTM not configured.");
            return;
        }

        const script = document.createElement("script");
        const noscript = document.createElement("noscript");

        script.async = true;

        script.innerHTML = `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${this.CONTAINER_ID}');
        `;
        noscript.innerHTML = `
            <iframe src="https://www.googletagmanager.com/ns.html?id=${this.CONTAINER_ID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>
        `;

        document.head.insertBefore(script, document.head.childNodes[0]);
        document.body.insertBefore(noscript, document.body.childNodes[0]);

        this.initialized = true;
    }

    initializeClient(config: GTMConfig) {
        if (this.initializedClient) {
            console.warn("Client GTM already initialized.");
            return;
        }

        if (!document) {
            console.warn("GTM requires a document.");
            return;
        }

        this.configureClient(config);

        if (!this.CLIENT_CONTAINER_ID) {
            console.warn("GTM not configured.");
            return;
        }

        const script = document.createElement("script");
        const noscript = document.createElement("noscript");

        script.async = true;

        script.innerHTML = `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${this.CLIENT_CONTAINER_ID}');
        `;
        noscript.innerHTML = `
            <iframe src="https://www.googletagmanager.com/ns.html?id=${this.CLIENT_CONTAINER_ID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>
        `;

        document.head.insertBefore(script, document.head.childNodes[0]);
        document.body.insertBefore(noscript, document.body.childNodes[0]);

        this.initializedClient = true;
    }

    push(...args: any[]) {
        if (!window) {
            console.warn("GTM requires a window.");
            return;
        }

        if (!(window as any).dataLayer) {
            (window as any).dataLayer = [];
        }

        (window as any).dataLayer.push(...args);
    }

    trackPageView() {
        this.push({ event: "pageview" });
    }

    trackCartRefresh() {
        this.push({ event: "cart_refresh" });
    }

    trackCartCleared(cart: Cart) {
        this.push({
            event: "remove_from_cart",
            ecommerce: {
                currency: "CAD",
                value:
                    cart.items.reduce((a, b) => a + b.department.price, 0) /
                    100,
                items: cart.items.map((it) => ({
                    item_id: it.department.id,
                    item_name: it.department.name,
                    price: it.department.price / 100,
                    quantity: 1,
                })),
            },
        });
    }

    trackItemViewed(dept: SimpleDepartment) {
        this.push({
            event: "view_item",
            ecommerce: {
                currency: "CAD",
                value: dept.price / 100,
                items: [
                    {
                        item_id: dept.id,
                        item_name: dept.name,
                        price: dept.price / 100,
                    },
                ],
            },
        });
    }

    trackBeginCheckout(cart: Cart) {
        this.push({
            event: "begin_checkout",
            ecommerce: {
                currency: "CAD",
                value:
                    (cart.items.reduce((a, b) => a + b.department.price, 0) +
                        cart.authFee +
                        cart.taxes.reduce((a, b) => a + b.amount, 0)) /
                    100,
                items: cart.items.map((it) => ({
                    item_id: it.department.id,
                    item_name: it.department.name,
                    price: it.department.price / 100,
                    quantity: 1,
                })),
            },
        });
    }

    trackAddShippingInfo(cart: Cart) {
        this.push({
            event: "add_shipping_info",
            ecommerce: {
                currency: "CAD",
                value:
                    (cart.items.reduce((a, b) => a + b.department.price, 0) +
                        cart.authFee +
                        cart.taxes.reduce((a, b) => a + b.amount, 0)) /
                    100,
                items: cart.items.map((it) => ({
                    item_id: it.department.id,
                    item_name: it.department.name,
                    price: it.department.price / 100,
                    quantity: 1,
                })),
            },
        });
    }

    trackItemAddedToCart(
        dept: SimpleDepartment,
        comp: boolean,
        quantity: number,
    ) {
        if (comp) return;
        this.push({
            event: "add_to_cart",
            ecommerce: {
                currency: "CAD",
                value: (dept.price * quantity) / 100,
                items: [
                    {
                        item_id: dept.id,
                        item_name: dept.name,
                        price: dept.price / 100,
                        quantity,
                    },
                ],
            },
        });
    }

    trackItemRemovedFromCart(
        dept: SimpleDepartment,
        comp: boolean,
        quantity: number,
    ) {
        if (comp) return;
        this.push({
            event: "remove_from_cart",
            ecommerce: {
                currency: "CAD",
                value: (dept.price * quantity) / 100,
                items: [
                    {
                        item_id: dept.id,
                        item_name: dept.name,
                        price: dept.price / 100,
                        quantity,
                    },
                ],
            },
        });
    }

    trackPurchase(order: Order) {
        this.push({
            event: "purchase",
            ecommerce: {
                currency: "CAD",
                value: order.total / 100,
                tax: order.taxes.reduce((a, b) => a + b.amount, 0) / 100,
                transaction_id: order.id,
                items: order.items.map((it) => ({
                    item_id: it.orderItemId,
                    item_name: it.department.name,
                    affiliation: order.client.name,
                    price: it.amount / 100,
                    quantity: 1,
                    coupon: it.discount ? it.discount.code : "",
                    discount: it.discount ? it.discount.amount / 100 : 0,
                })),
            },
        });
    }
}

export const gtm = new GTM();
